import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@heycater/design-system';
import {
  add,
  addWeeks,
  format,
  getWeek,
  isFirstDayOfMonth,
  isSameMonth,
} from 'date-fns';
import { useRouter } from 'next/router';
import {
  CaptionProps,
  Day,
  DayPicker,
  DayProps,
  IconLeft,
  IconRight,
  Row,
  RowProps,
  useActiveModifiers,
  useDayPicker,
  useNavigation,
} from 'react-day-picker';
import styled from 'styled-components';

import 'react-day-picker/dist/style.css';

import DatePickerLayout from 'qualification/components/question_types/DatePicker/DatePickerLayout';
import DatePickerSide from 'qualification/components/question_types/DatePicker/DatePickerSide';
import { useQualification } from 'qualification/context';
import {
  dateQuestion,
  DEFAULT_LEAD_DAYS,
  isRecurringQuestion,
} from 'qualification/schema/questions/eventDateQuestion';
import { localeToDateLocaleMapping } from 'shared/helpers/dates';

const defaultLeadTimeInDays = DEFAULT_LEAD_DAYS;

function DatePickerEmbedded() {
  const router = useRouter();

  const { answerOf, actions, state } = useQualification();
  const answer = answerOf('event_date');
  const date = answer;
  const activeDate = date ? new Date(date) : undefined;
  const minimumDate = add(new Date(), {
    days: defaultLeadTimeInDays,
  });

  const attributes =
    'qualification' in state
      ? dateQuestion.getAttributes({
          answers: state.qualification.answers,
          selectedCaterer: state.qualification.selectedCaterer,
        })
      : null;

  const minDateMessage =
    router.locale === 'en'
      ? attributes?.minDate?.messageEn
      : attributes?.minDate?.messageDe;

  const [isRecurringNeedSelected] = useState(
    !!answerOf(isRecurringQuestion.id)
  );

  useEffect(() => {
    if (date) {
      actions.answerQuestion('has_recurring_need', isRecurringNeedSelected);
    }
  }, [isRecurringNeedSelected]);

  const [month, setMonth] = useState(activeDate || minimumDate);
  return (
    <DatePickerLayout
      dateSelector={
        <StyledDateSelect>
          <DateSelectWrapper>
            <DayPicker
              components={{
                Caption: CustomCaption,
                Row: OnlyAfterToday,
                Day: DayWithOutsideMonthLabel,
              }}
              formatters={{
                formatWeekdayName: (day, options) =>
                  format(day, 'EEEEE', { locale: options?.locale }),
              }}
              month={month}
              onDayClick={(date) => actions.answerQuestion('event_date', date)}
              selected={activeDate}
              fromDate={minimumDate}
              onMonthChange={setMonth}
              captionLayout="buttons"
              locale={
                router.locale
                  ? localeToDateLocaleMapping[
                      router.locale as keyof typeof localeToDateLocaleMapping
                    ]
                  : undefined
              }
              showOutsideDays
              mode="single"
            />
          </DateSelectWrapper>
          {minDateMessage && (
            <Box textAlign="center">
              <TypographyCaption>*{minDateMessage}</TypographyCaption>
            </Box>
          )}
        </StyledDateSelect>
      }
      sideContent={<DatePickerSide />}
    />
  );
}

function DayWithOutsideMonthLabel(props: DayProps) {
  const { outside } = useActiveModifiers(props.date, props.displayMonth);
  const daypicker = useDayPicker();

  if (outside && isFirstDayOfMonth(props.date)) {
    return (
      <Box position="relative">
        <Box position="absolute" top="-3px" left="-3px">
          <Typography fontSize={9}>
            {format(props.date, 'MMM', { locale: daypicker.locale })}
          </Typography>
        </Box>
        <Day {...props} />
      </Box>
    );
  }

  return <Day {...props} />;
}

function CustomCaption(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const daypicker = useDayPicker();
  return (
    <Box display="flex" justifyContent="space-between" py={1}>
      <MonthNavigationButton
        type="button"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
      >
        <IconLeft />
      </MonthNavigationButton>
      <Typography fontSize={14}>
        {format(props.displayMonth, 'MMMM', { locale: daypicker.locale })}
      </Typography>
      <MonthNavigationButton
        type="button"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      >
        <IconRight />
      </MonthNavigationButton>
    </Box>
  );
}

function OnlyAfterToday(props: RowProps) {
  const today = new Date();

  const isFirstMonth = isSameMonth(today, props.displayMonth);

  if (isFirstMonth) {
    /**
     * Offset the first month, so the fromDate is shown in the first row
     */
    const firstWeekOfMonth = getWeek(props.displayMonth);
    const weekOfFirstSelectableDay = getWeek(today);
    const weekOffset = weekOfFirstSelectableDay - firstWeekOfMonth;
    const datesWithWeekOffset = props.dates.map((date) =>
      addWeeks(date, weekOffset)
    );

    return (
      <Row
        {...props}
        weekNumber={props.weekNumber + weekOffset}
        dates={datesWithWeekOffset}
      />
    );
  }

  return <Row {...props} />;
}

const MonthNavigationButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const TypographyCaption = styled(Typography)`
  color: #686d6b;
  font-size: 12px;
  line-height: 17px;
  letter-spacing: 0.4px;
`;

const StyledDateSelect = styled.div`
  width: 400px;
  max-width: 100%;
  margin: ${(props) => props.theme.spacing(2, 'auto', 3, 'auto')};
  margin: 0 auto;
  padding: 0;
  .rdp {
    --rdp-accent-color: ${({ theme }) => theme.palette.secondary.light};
    --rdp-background-color: #a3cb1861;
    --rdp-cell-size: 35px;

    background-color: white;
    border-radius: 8px;
    padding: ${({ theme }) => theme.spacing(1.5, 2, 2, 2)};
    margin: 0 auto;
  }

  .rdp-head_cell {
    font-size: 14px;
    font-weight: 400;
    padding-bottom: ${({ theme }) => theme.spacing(2)}px;
    padding-top: ${({ theme }) => theme.spacing(2.5)}px;
  }

  .rdp-button {
    transition: background-color 0.2s ease;
  }

  .rdp-day_outside {
    opacity: 0.8;
  }
`;

const DateSelectWrapper = styled.div`
  width: fit-content;
  margin: 0 auto;
`;

export default DatePickerEmbedded;
